import { defineStore } from "pinia"
import { computed, inject, ref, shallowRef } from "vue"
import { useToast } from "primevue/usetoast"
import { ServiceError } from "@core/exceptions"
import type { Building, BuildingVisibleToEntry } from "@types"
import type { Services } from "@services"
import type {
    HomeBuildingNode,
    FolderListRow,
    HomeWorkspaceModalState,
    RootPagination,
} from "@modules/home/stores/home-list.types"

const DEFAULT_PAGE_LIMIT = 15

/**
 * Compare deux parents dossier (racine = null / undefined).
 */
function isSameFolderParent(
    a: string | null | undefined,
    b: string | null | undefined
): boolean {
    return (a ?? null) === (b ?? null)
}

/**
 * Tri aligné sur l’API dossiers (nom ou date de création).
 */
function sortFolderListRows(
    rows: FolderListRow[],
    sort: string,
    order: "asc" | "desc"
): FolderListRow[] {
    const mult = order === "asc" ? 1 : -1
    const list = [...rows]
    if (sort === "name") {
        list.sort(
            (a, b) =>
                mult *
                a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        )
    } else {
        list.sort((a, b) => {
            const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
            const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
            return mult * (ta - tb)
        })
    }
    return list
}

/**
 * Message utilisateur à partir d’une erreur API (déplacement dossier / bâtiment).
 */
function getWorkspaceMoveErrorMessage(err: unknown): string {
    if (err instanceof ServiceError) return err.message
    if (err instanceof Error) return err.message
    return "Une erreur est survenue"
}

/**
 * Identifiant affiché / API pour un nœud bâtiment dashboard.
 */
function getHomeBuildingId(node: HomeBuildingNode): string {
    const b = node as Building
    return String(b._id_building ?? b._id ?? "")
}

/**
 * Met à jour le nom d’un bâtiment dans l’arbre (copie immuable sur le chemin touché).
 */
function patchHomeBuildingName(
    nodes: HomeBuildingNode[],
    buildingId: string,
    name: string
): HomeBuildingNode[] {
    return nodes.map((node) => {
        if (getHomeBuildingId(node) === buildingId) {
            return { ...node, name }
        }
        const children = (node.children ?? []) as HomeBuildingNode[]
        if (!children.length) {
            return node
        }
        return {
            ...node,
            children: patchHomeBuildingName(children, buildingId, name),
        }
    })
}

/**
 * Met à jour les étiquettes d’un bâtiment dans l’arbre (copie immuable).
 */
function patchHomeBuildingTags(
    nodes: HomeBuildingNode[],
    buildingId: string,
    tagNames: string[]
): HomeBuildingNode[] {
    const tags = tagNames.map((name) => ({ name }))
    return nodes.map((node) => {
        if (getHomeBuildingId(node) === buildingId) {
            return { ...node, tags }
        }
        const children = (node.children ?? []) as HomeBuildingNode[]
        if (!children.length) {
            return node
        }
        return {
            ...node,
            children: patchHomeBuildingTags(children, buildingId, tagNames),
        }
    })
}

/**
 * Applique la même liste `visible_to` au nœud et à tout son sous-arbre
 * (aligné sur la propagation des accès côté API pour les bâtiments enfants).
 */
function applyVisibleToToNodeAndDescendants(
    node: HomeBuildingNode,
    visibleTo: BuildingVisibleToEntry[]
): HomeBuildingNode {
    const children = (node.children ?? []) as HomeBuildingNode[]
    const nextChildren = children.map((child) =>
        applyVisibleToToNodeAndDescendants(child, visibleTo)
    )
    return {
        ...node,
        visible_to: visibleTo,
        children: nextChildren,
    }
}

/**
 * Met à jour `visible_to` pour le bâtiment ciblé et récursivement pour ses enfants (même liste).
 */
function patchHomeBuildingVisibleToSubtree(
    nodes: HomeBuildingNode[],
    rootBuildingId: string,
    visibleTo: BuildingVisibleToEntry[]
): HomeBuildingNode[] {
    return nodes.map((node) => {
        const id = getHomeBuildingId(node)
        const children = (node.children ?? []) as HomeBuildingNode[]

        if (id === rootBuildingId) {
            return applyVisibleToToNodeAndDescendants(node, visibleTo)
        }

        if (children.length) {
            return {
                ...node,
                children: patchHomeBuildingVisibleToSubtree(
                    children,
                    rootBuildingId,
                    visibleTo
                ),
            }
        }

        return node
    })
}

/**
 * Nombre de nœuds bâtiment dans un sous-arbre (racine + descendants).
 */
function countHomeBuildingSubtreeNodes(node: HomeBuildingNode): number {
    const children = (node.children ?? []) as HomeBuildingNode[]
    if (!children.length) return 1
    return (
        1 +
        children.reduce((sum, c) => sum + countHomeBuildingSubtreeNodes(c), 0)
    )
}

/**
 * Trouve le nœud bâtiment (y compris en profondeur) par identifiant.
 */
function findHomeBuildingSubtree(
    nodes: HomeBuildingNode[],
    buildingId: string
): HomeBuildingNode | null {
    for (const node of nodes) {
        if (getHomeBuildingId(node) === buildingId) {
            return node
        }
        const children = (node.children ?? []) as HomeBuildingNode[]
        if (children.length) {
            const found = findHomeBuildingSubtree(children, buildingId)
            if (found) return found
        }
    }
    return null
}

/**
 * Retire un bâtiment et son sous-arbre de la liste (copie immuable).
 */
function removeHomeBuildingSubtree(
    nodes: HomeBuildingNode[],
    rootId: string
): { next: HomeBuildingNode[]; removed: boolean } {
    const out: HomeBuildingNode[] = []
    let removed = false
    for (const node of nodes) {
        if (getHomeBuildingId(node) === rootId) {
            removed = true
            continue
        }
        const children = (node.children ?? []) as HomeBuildingNode[]
        if (children.length) {
            const { next: newChildren, removed: childRemoved } =
                removeHomeBuildingSubtree(children, rootId)
            if (childRemoved) {
                removed = true
                out.push({ ...node, children: newChildren })
                continue
            }
        }
        out.push(node)
    }
    return { next: out, removed }
}

/**
 * Aplatit un arbre de bâtiments en lignes avec profondeur (affichage liste).
 */
export function flattenBuildingTree(
    nodes: HomeBuildingNode[],
    depth = 0,
    parentId: string | null = null
): Array<{
    node: HomeBuildingNode
    depth: number
    parentId: string | null
}> {
    const out: Array<{
        node: HomeBuildingNode
        depth: number
        parentId: string | null
    }> = []
    for (const node of nodes) {
        out.push({ node, depth, parentId })
        if (node.children?.length) {
            const id =
                (node as Building & { _id?: string })._id ??
                (node as Building & { _id_building?: string })._id_building ??
                ""
            out.push(
                ...flattenBuildingTree(
                    node.children as HomeBuildingNode[],
                    depth + 1,
                    id
                )
            )
        }
    }
    return out
}

/**
 * Store listes accueil / dossiers / recherche (dashboard legacy).
 */
export const useHomeWorkspaceStore = defineStore("homeWorkspace", () => {
    const toast = useToast()

    const {
        $getBuildings,
        $getTags,
        $getFolders,
        $getFolderBuildings,
        $moveFolder,
        $moveFolderBuilding,
        $renameFolder,
        $deleteFolder,
        $createFolder,
    } = inject<Services>("services")!

    const rootBuildings = shallowRef<HomeBuildingNode[]>([])
    const rootPagination = ref<RootPagination>({
        page: 1,
        limit: DEFAULT_PAGE_LIMIT,
        total: 0,
        hasMore: false,
    })
    const rootSort = ref("createdAt")
    const rootOrder = ref<"asc" | "desc">("asc")
    const rootTags = ref<string[]>([])
    const rootSearch = ref<string | null>(null)

    const folderBuildings = shallowRef<HomeBuildingNode[]>([])
    const folderBuildingsPagination = ref<RootPagination>({
        page: 1,
        limit: DEFAULT_PAGE_LIMIT,
        total: 0,
        hasMore: false,
    })
    const folderBuildingSort = ref("createdAt")
    const folderBuildingOrder = ref<"asc" | "desc">("asc")
    const folderBuildingTags = ref<string[]>([])

    const folders = shallowRef<FolderListRow[]>([])
    const foldersPagination = ref<RootPagination>({
        page: 1,
        limit: DEFAULT_PAGE_LIMIT,
        total: 0,
        hasMore: false,
    })
    const folderSort = ref("createdAt")
    const folderOrder = ref<"asc" | "desc">("desc")
    const folderSearch = ref<string | null>(null)

    const breadcrumb = ref<Array<{ _id?: string; name?: string }>>([])
    const currentFolderId = ref<string | null>(null)

    const companyTags = ref<string[]>([])

    /**
     * Liste bâtiments affichée : `root` (page « tous les bâtiments ») vs `folder` (mes dossiers).
     * Mis à jour par `HomeBuildingList` au montage.
     */
    const buildingsListContext = ref<"root" | "folder">("folder")

    const loadingRoot = ref(false)
    const loadingFolderBuildings = ref(false)
    /** Rechargement complet (reset) : la liste racine peut rester affichée jusqu’à la réponse. */
    const refreshingRoot = ref(false)
    /** Rechargement complet (reset) dossier : `folderBuildings` est vidé pour les skeletons. */
    const refreshingFolderBuildings = ref(false)
    const loadingFolders = ref(false)

    const modal = ref<HomeWorkspaceModalState>({
        kind: null,
        meta: {},
    })

    let abortRoot: AbortController | null = null
    let abortFolderBuildings: AbortController | null = null
    let abortFolders: AbortController | null = null
    /** Évite de remettre `refreshing*` à false quand une requête plus récente a déjà repris le relais. */
    let rootFetchGeneration = 0
    let folderBuildingsFetchGeneration = 0
    let foldersFetchGeneration = 0

    const getFlattenedRootRows = computed(() =>
        flattenBuildingTree(rootBuildings.value)
    )

    const getFlattenedFolderBuildingRows = computed(() =>
        flattenBuildingTree(folderBuildings.value)
    )

    function openModal(
        kind: NonNullable<HomeWorkspaceModalState["kind"]>,
        meta: Record<string, unknown> = {}
    ): void {
        modal.value = { kind, meta }
    }

    function closeModal(): void {
        modal.value = { kind: null, meta: {} }
    }

    /**
     * Vide listes bâtiments / dossiers avant navigation vers une page bâtiment.
     * Invalide les réponses API en cours pour éviter de réinjecter des données périmées au retour.
     */
    function clearWorkspaceListsOnBuildingNavigation(): void {
        rootFetchGeneration += 1
        folderBuildingsFetchGeneration += 1
        foldersFetchGeneration += 1

        if (abortRoot) {
            abortRoot.abort()
            abortRoot = null
        }
        if (abortFolderBuildings) {
            abortFolderBuildings.abort()
            abortFolderBuildings = null
        }
        if (abortFolders) {
            abortFolders.abort()
            abortFolders = null
        }

        rootBuildings.value = []
        folderBuildings.value = []
        folders.value = []
        breadcrumb.value = []

        rootPagination.value = {
            page: 1,
            limit: DEFAULT_PAGE_LIMIT,
            total: 0,
            hasMore: false,
        }
        folderBuildingsPagination.value = {
            page: 1,
            limit: DEFAULT_PAGE_LIMIT,
            total: 0,
            hasMore: false,
        }
        foldersPagination.value = {
            page: 1,
            limit: DEFAULT_PAGE_LIMIT,
            total: 0,
            hasMore: false,
        }

        refreshingRoot.value = false
        refreshingFolderBuildings.value = false
        loadingRoot.value = false
        loadingFolderBuildings.value = false
        loadingFolders.value = false
    }

    async function loadCompanyTags(): Promise<void> {
        try {
            companyTags.value = await $getTags()
        } catch {
            companyTags.value = []
        }
    }

    async function fetchRootBuildingsPage(
        options: {
            reset?: boolean
            tags?: string[]
            search?: string | null
            sort?: string
            order?: "asc" | "desc"
        } = {}
    ): Promise<void> {
        if (abortRoot) abortRoot.abort()
        abortRoot = new AbortController()
        const fetchGeneration = ++rootFetchGeneration
        const { reset = false, tags, search, sort, order } = options
        if (tags !== undefined) rootTags.value = tags
        if (search !== undefined) rootSearch.value = search
        if (sort) rootSort.value = sort
        if (order) rootOrder.value = order

        if (reset) {
            refreshingRoot.value = true
            rootPagination.value = {
                ...rootPagination.value,
                page: 1,
                hasMore: false,
            }
        }

        loadingRoot.value = true
        try {
            const page = reset ? 1 : rootPagination.value.page
            const res = await $getBuildings({
                sort: rootSort.value,
                order: rootOrder.value,
                tags: rootTags.value.length ? rootTags.value : null,
                search: rootSearch.value || null,
                tree: true,
                page,
                limit: rootPagination.value.limit,
                signal: abortRoot.signal,
            })
            if (fetchGeneration !== rootFetchGeneration) return
            const chunk = (res.data ?? []) as HomeBuildingNode[]
            if (reset) {
                rootBuildings.value = chunk
            } else {
                rootBuildings.value = [...rootBuildings.value, ...chunk]
            }
            rootPagination.value = {
                ...rootPagination.value,
                page: page + 1,
                total: res.total ?? 0,
                hasMore: !!res.has_more,
            }
        } finally {
            loadingRoot.value = false
            if (reset && fetchGeneration === rootFetchGeneration) {
                refreshingRoot.value = false
            }
        }
    }

    async function fetchFolderBuildingsPage(options: {
        folderId: string | null
        reset?: boolean
        tags?: string[]
        sort?: string
        order?: "asc" | "desc"
    }): Promise<void> {
        if (abortFolderBuildings) abortFolderBuildings.abort()
        abortFolderBuildings = new AbortController()
        const fetchGeneration = ++folderBuildingsFetchGeneration
        const { folderId, reset = false, tags, sort, order } = options
        currentFolderId.value = folderId
        if (tags !== undefined) folderBuildingTags.value = tags
        if (sort) folderBuildingSort.value = sort
        if (order) folderBuildingOrder.value = order

        if (reset) {
            refreshingFolderBuildings.value = true
            folderBuildingsPagination.value = {
                ...folderBuildingsPagination.value,
                page: 1,
                hasMore: false,
            }
            folderBuildings.value = []
        }

        loadingFolderBuildings.value = true
        try {
            const page = reset ? 1 : folderBuildingsPagination.value.page
            const res = await $getFolderBuildings({
                folderId,
                sort: folderBuildingSort.value,
                order: folderBuildingOrder.value,
                tags: folderBuildingTags.value.length
                    ? folderBuildingTags.value
                    : null,
                page,
                limit: folderBuildingsPagination.value.limit,
                signal: abortFolderBuildings.signal,
            })
            if (fetchGeneration !== folderBuildingsFetchGeneration) return
            const chunk = (res.data ?? []) as HomeBuildingNode[]
            if (reset) {
                folderBuildings.value = chunk
            } else {
                folderBuildings.value = [...folderBuildings.value, ...chunk]
            }
            folderBuildingsPagination.value = {
                ...folderBuildingsPagination.value,
                page: page + 1,
                total: res.total ?? 0,
                hasMore: !!res.has_more,
            }
        } finally {
            loadingFolderBuildings.value = false
            if (reset && fetchGeneration === folderBuildingsFetchGeneration) {
                refreshingFolderBuildings.value = false
            }
        }
    }

    async function fetchFoldersPage(options: {
        folderId: string | null
        reset?: boolean
        search?: string | null
        sort?: string
        order?: "asc" | "desc"
    }): Promise<void> {
        if (abortFolders) abortFolders.abort()
        abortFolders = new AbortController()
        const fetchGeneration = ++foldersFetchGeneration
        const { folderId, reset = false, search, sort, order } = options
        if (search !== undefined) folderSearch.value = search
        if (sort) folderSort.value = sort
        if (order) folderOrder.value = order

        if (reset) {
            foldersPagination.value = {
                ...foldersPagination.value,
                page: 1,
                total: 0,
                hasMore: false,
            }
            folders.value = []
        }

        loadingFolders.value = true
        try {
            const page = reset ? 1 : foldersPagination.value.page
            const res = await $getFolders(folderId, {
                sort: folderSort.value,
                order: folderOrder.value,
                search: folderSearch.value,
                signal: abortFolders.signal,
            })
            if (fetchGeneration !== foldersFetchGeneration) return
            folders.value = res.data
            breadcrumb.value = res.path ?? []
            foldersPagination.value = {
                ...foldersPagination.value,
                page: page + 1,
                total: res.total,
                hasMore: res.data.length >= foldersPagination.value.limit,
            }
        } finally {
            loadingFolders.value = false
        }
    }

    function registerBuildingsListContext(kind: "root" | "folder"): void {
        buildingsListContext.value = kind
    }

    async function submitMoveFolderBuilding(
        buildingIds: string[],
        folderId: string | null
    ): Promise<void> {
        const rootDragId = buildingIds[0]
        if (!rootDragId) return

        const useRootList = buildingsListContext.value === "root"
        const listRef = useRootList ? rootBuildings : folderBuildings
        const paginationRef = useRootList
            ? rootPagination
            : folderBuildingsPagination

        const previousTrees = structuredClone(
            listRef.value
        ) as HomeBuildingNode[]
        const previousPagination = { ...paginationRef.value }

        const { next, removed } = removeHomeBuildingSubtree(
            listRef.value,
            rootDragId
        )
        const movedCount = buildingIds.length

        if (removed) {
            listRef.value = next
            paginationRef.value = {
                ...paginationRef.value,
                total: Math.max(0, paginationRef.value.total - movedCount),
            }
        }

        try {
            await $moveFolderBuilding({ buildingIds, folderId })
            closeModal()
            const n = buildingIds.length
            toast.add({
                group: "notifications",
                severity: "success",
                summary: n > 1 ? "Bâtiments déplacés" : "Bâtiment déplacé",
                detail:
                    n > 1
                        ? `${n} bâtiments ont été déplacés dans le dossier cible.`
                        : "Le bâtiment a été déplacé dans le dossier cible.",
                life: 4000,
            })
        } catch (err) {
            if (removed) {
                listRef.value = previousTrees
                paginationRef.value = previousPagination
            }
            toast.add({
                group: "notifications",
                severity: "error",
                summary: "Déplacement impossible",
                detail: getWorkspaceMoveErrorMessage(err),
                life: 6000,
            })
        }
    }

    async function submitMoveFolder(
        fromFolderId: string,
        toFolderId: string | null,
        company = false
    ): Promise<void> {
        const previousFolders = folders.value.slice()
        const previousPagination = { ...foldersPagination.value }

        const beforeLen = folders.value.length
        folders.value = folders.value.filter((f) => f._id !== fromFolderId)
        const removedOptimistic = folders.value.length < beforeLen
        if (removedOptimistic) {
            foldersPagination.value = {
                ...foldersPagination.value,
                total: Math.max(0, foldersPagination.value.total - 1),
            }
        }

        try {
            await $moveFolder({ fromFolderId, toFolderId, company })
            closeModal()
            toast.add({
                group: "notifications",
                severity: "success",
                summary: "Dossier déplacé",
                detail: "Le dossier a bien été déplacé.",
                life: 4000,
            })
            if (folderSearch.value?.trim()) {
                await fetchFoldersPage({
                    folderId: currentFolderId.value,
                    reset: true,
                })
            }
        } catch (err) {
            if (removedOptimistic) {
                folders.value = previousFolders
                foldersPagination.value = previousPagination
            }
            toast.add({
                group: "notifications",
                severity: "error",
                summary: "Déplacement impossible",
                detail: getWorkspaceMoveErrorMessage(err),
                life: 6000,
            })
        }
    }

    async function submitRenameFolder(
        folderId: string,
        name: string
    ): Promise<void> {
        const trimmed = name.trim()
        await $renameFolder({ folderId, name: trimmed })
        closeModal()
        folders.value = sortFolderListRows(
            folders.value.map((f) =>
                f._id === folderId ? { ...f, name: trimmed } : f
            ),
            folderSort.value,
            folderOrder.value
        )
        breadcrumb.value = breadcrumb.value.map((b) =>
            b._id === folderId ? { ...b, name: trimmed } : b
        )
    }

    /**
     * Après renommage API : met à jour le libellé dans les arbres bâtiments chargés.
     */
    function applyBuildingRename(buildingId: string, name: string): void {
        rootBuildings.value = patchHomeBuildingName(
            rootBuildings.value,
            buildingId,
            name
        )
        folderBuildings.value = patchHomeBuildingName(
            folderBuildings.value,
            buildingId,
            name
        )
    }

    /**
     * Après ajout / suppression d’étiquettes côté API : met à jour les tags du nœud dans les arbres chargés.
     */
    function applyBuildingTagsUpdate(
        buildingId: string,
        tagNames: string[]
    ): void {
        rootBuildings.value = patchHomeBuildingTags(
            rootBuildings.value,
            buildingId,
            tagNames
        )
        folderBuildings.value = patchHomeBuildingTags(
            folderBuildings.value,
            buildingId,
            tagNames
        )
    }

    /**
     * Après mise à jour des accès (partage) : met à jour `visible_to` dans les arbres chargés,
     * y compris sur tous les bâtiments enfants du nœud ciblé (même liste d’accès).
     */
    function applyBuildingAccessUpdate(
        buildingId: string,
        visibleTo: BuildingVisibleToEntry[]
    ): void {
        rootBuildings.value = patchHomeBuildingVisibleToSubtree(
            rootBuildings.value,
            buildingId,
            visibleTo
        )
        folderBuildings.value = patchHomeBuildingVisibleToSubtree(
            folderBuildings.value,
            buildingId,
            visibleTo
        )
    }

    /**
     * Après suppression API : retire le sous-arbre des listes chargées et ajuste les totaux
     * (pagination infinie), sans refetch.
     */
    function applyBuildingDeleted(buildingId: string): void {
        const rootNode = findHomeBuildingSubtree(
            rootBuildings.value,
            buildingId
        )
        const rootRemovedCount = rootNode
            ? countHomeBuildingSubtreeNodes(rootNode)
            : 0
        const rootRes = removeHomeBuildingSubtree(
            rootBuildings.value,
            buildingId
        )
        if (rootRes.removed) {
            rootBuildings.value = rootRes.next
            const nextRootTotal = Math.max(
                0,
                rootPagination.value.total - rootRemovedCount
            )
            rootPagination.value = {
                ...rootPagination.value,
                total: nextRootTotal,
                hasMore:
                    nextRootTotal > 0 ? rootPagination.value.hasMore : false,
            }
        }

        const folderNode = findHomeBuildingSubtree(
            folderBuildings.value,
            buildingId
        )
        const folderRemovedCount = folderNode
            ? countHomeBuildingSubtreeNodes(folderNode)
            : 0
        const folderRes = removeHomeBuildingSubtree(
            folderBuildings.value,
            buildingId
        )
        if (folderRes.removed) {
            folderBuildings.value = folderRes.next
            const nextFolderTotal = Math.max(
                0,
                folderBuildingsPagination.value.total - folderRemovedCount
            )
            folderBuildingsPagination.value = {
                ...folderBuildingsPagination.value,
                total: nextFolderTotal,
                hasMore:
                    nextFolderTotal > 0
                        ? folderBuildingsPagination.value.hasMore
                        : false,
            }
        }
    }

    /**
     * Après mise à jour des accès dossier : met à jour `visible_to` / `access` sur la ligne dossier listée.
     */
    function applyFolderAccessUpdate(
        folderId: string,
        visibleTo: BuildingVisibleToEntry[]
    ): void {
        folders.value = folders.value.map((f) =>
            f._id === folderId
                ? { ...f, visible_to: visibleTo, access: visibleTo }
                : f
        )
    }

    async function submitDeleteFolder(folderId: string): Promise<void> {
        await $deleteFolder(folderId)
        closeModal()
        const before = folders.value.length
        folders.value = folders.value.filter((f) => f._id !== folderId)
        if (folders.value.length < before) {
            foldersPagination.value = {
                ...foldersPagination.value,
                total: Math.max(0, foldersPagination.value.total - 1),
            }
        }
    }

    async function submitCreateFolder(
        name: string,
        parentFolderId?: string | null
    ): Promise<void> {
        const created = await $createFolder({ name, folderId: parentFolderId })
        closeModal()
        if (folderSearch.value?.trim()) {
            await fetchFoldersPage({
                folderId: currentFolderId.value,
                reset: true,
            })
            return
        }
        if (!isSameFolderParent(parentFolderId, currentFolderId.value)) {
            return
        }
        const row: FolderListRow = {
            ...created,
            createdAt: created.createdAt ?? new Date().toISOString(),
        }
        const merged = [...folders.value, row]
        folders.value = sortFolderListRows(
            merged,
            folderSort.value,
            folderOrder.value
        )
        foldersPagination.value = {
            ...foldersPagination.value,
            total: foldersPagination.value.total + 1,
        }
    }

    return {
        rootBuildings,
        rootPagination,
        rootSort,
        rootOrder,
        rootTags,
        rootSearch,
        getFlattenedRootRows,
        folderBuildings,
        folderBuildingsPagination,
        folderBuildingSort,
        folderBuildingOrder,
        folderBuildingTags,
        getFlattenedFolderBuildingRows,
        folders,
        foldersPagination,
        folderSort,
        folderOrder,
        folderSearch,
        breadcrumb,
        currentFolderId,
        companyTags,
        loadingRoot,
        loadingFolderBuildings,
        refreshingRoot,
        refreshingFolderBuildings,
        loadingFolders,
        modal,
        openModal,
        closeModal,
        clearWorkspaceListsOnBuildingNavigation,
        loadCompanyTags,
        fetchRootBuildingsPage,
        fetchFolderBuildingsPage,
        fetchFoldersPage,
        registerBuildingsListContext,
        submitMoveFolderBuilding,
        submitMoveFolder,
        submitRenameFolder,
        applyBuildingRename,
        applyBuildingTagsUpdate,
        applyBuildingAccessUpdate,
        applyBuildingDeleted,
        applyFolderAccessUpdate,
        submitDeleteFolder,
        submitCreateFolder,
    }
})
